import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { ArrowLeft, Sun, Moon, Trophy, Hash, Calendar, RotateCcw, Sparkles, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../context/AuthContext';

const tg = () => window.Telegram?.WebApp;

function DrawResults() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    fetchResults();
    const telegram = tg();
    if (telegram) {
      telegram.BackButton.show();
      telegram.BackButton.onClick(() => navigate('/'));
    }
    return () => { if (tg()) { tg().BackButton.hide(); } };
  }, []);

  useEffect(() => {
    if (!user || !token) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/user/loyalty`, { headers: { Authorization: `Bearer ${token}` } });
        setLoyalty(r.data);
      } catch { /* silent */ }
    })();
  }, [user, token]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/draws/results?limit=30`);
      setResults(response.data.results || []);
    } catch (e) {
      console.error('Error fetching results:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-6">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-700/50 backdrop-blur-lg bg-slate-900/80">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="back-button" onClick={() => navigate('/')} className="text-slate-400 hover:text-white p-1">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-base font-bold text-white leading-tight">Draw Results</h1>
              <p className="text-[11px] text-slate-500">Illinois Pick 3 Fireball History</p>
            </div>
          </div>
          <Button data-testid="refresh-results" onClick={fetchResults} size="sm" variant="ghost" className="text-slate-400 h-8 px-2">
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Loyalty Nudge — post-win conversion */}
      {user && loyalty && (() => {
        const iWon = results.some((r) => (r.winners || []).some((w) => w.username === user.username));
        const { is_max_tier, current_tier, current_fee_pct, next_tier, next_tier_fee_pct, games_to_next_tier, next_tier_min_games, completed_games } = loyalty;
        const pct = is_max_tier ? 100 : Math.min(100, (completed_games / (next_tier_min_games || 1)) * 100);
        return (
          <div className="px-4 pt-3">
            <div
              data-testid="post-win-loyalty-nudge"
              className={`rounded-2xl border p-3 flex items-center gap-3 ${
                is_max_tier
                  ? 'border-emerald-500/40 bg-gradient-to-br from-emerald-500/15 to-teal-500/5'
                  : iWon
                    ? 'border-amber-500/50 bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-transparent'
                    : 'border-sky-500/30 bg-gradient-to-br from-sky-500/10 to-blue-500/5'
              }`}
            >
              <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                is_max_tier ? 'bg-emerald-500/20' : iWon ? 'bg-amber-500/25' : 'bg-sky-500/15'
              }`}>
                {is_max_tier
                  ? <Sparkles className="w-5 h-5 text-emerald-300" />
                  : iWon
                    ? <Trophy className="w-5 h-5 text-amber-300" />
                    : <TrendingDown className="w-5 h-5 text-sky-300" />}
              </div>
              <div className="flex-1 min-w-0">
                {is_max_tier ? (
                  <>
                    <p className="text-sm font-bold text-emerald-300 leading-tight">🎉 Max loyalty tier — 3.21%</p>
                    <p className="text-[11px] text-slate-300 leading-snug mt-0.5">
                      <span className="text-white font-semibold">{completed_games} games</span> completed. Cheapest XRP room fees in the app — nice.
                    </p>
                  </>
                ) : iWon ? (
                  <>
                    <p className="text-sm font-bold text-amber-300 leading-tight">🎉 Congrats on the win!</p>
                    <p className="text-[11px] text-slate-300 leading-snug mt-0.5">
                      <span className="text-white font-bold">{games_to_next_tier} more {games_to_next_tier === 1 ? 'game' : 'games'}</span> to drop your XRP-room fee from {current_fee_pct.toFixed(2)}% to <span className="text-emerald-300 font-semibold">{next_tier_fee_pct.toFixed(2)}%</span> (Tier {next_tier}).
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-bold text-sky-300 leading-tight">Tier {current_tier} · {current_fee_pct.toFixed(2)}% fee</p>
                    <p className="text-[11px] text-slate-300 leading-snug mt-0.5">
                      <span className="text-white font-semibold">{games_to_next_tier} more {games_to_next_tier === 1 ? 'game' : 'games'}</span> to Tier {next_tier} at <span className="text-emerald-300 font-semibold">{next_tier_fee_pct.toFixed(2)}%</span>.
                    </p>
                  </>
                )}
                {!is_max_tier && (
                  <div className="mt-2 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${iWon ? 'bg-gradient-to-r from-amber-500 via-emerald-400 to-emerald-400' : 'bg-gradient-to-r from-sky-500 via-amber-400 to-emerald-400'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                )}
              </div>
              {!is_max_tier && (
                <button
                  data-testid="post-win-loyalty-cta"
                  onClick={() => navigate('/')}
                  className="flex-shrink-0 text-[11px] font-bold text-orange-300 hover:text-orange-200 whitespace-nowrap"
                >
                  Play →
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Results List */}
      <div className="px-4 pt-4">
        {results.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No draw results yet</p>
            <p className="text-[11px] text-slate-600 mt-1">Results will appear here after each Midday &amp; Evening draw</p>
          </div>
        ) : (
          <div className="space-y-3">
            {results.map((result, idx) => (
              <div
                key={idx}
                data-testid={`draw-result-${idx}`}
                className="bg-slate-800/40 border border-slate-700/40 rounded-2xl overflow-hidden"
              >
                {/* Result Header */}
                <div className="px-4 py-3 flex items-center justify-between border-b border-slate-700/30">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      result.draw_type === 'midday' ? 'bg-amber-500/15' : 'bg-indigo-500/15'
                    }`}>
                      {result.draw_type === 'midday'
                        ? <Sun className="w-4 h-4 text-amber-400" />
                        : <Moon className="w-4 h-4 text-indigo-400" />}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-white capitalize">{result.draw_type} Draw</p>
                      <p className="text-[10px] text-slate-500">{formatDate(result.draw_date)}</p>
                    </div>
                  </div>
                  {/* Fireball Number */}
                  <div className="flex items-center gap-1.5">
                    <Hash className="w-3 h-3 text-slate-500" />
                    <span className="text-2xl font-black text-orange-400 font-mono">{result.fireball_number}</span>
                  </div>
                </div>

                {/* Winners */}
                {result.winners && result.winners.length > 0 ? (
                  <div className="px-4 py-2.5 space-y-1.5">
                    {result.winners.map((winner, wIdx) => (
                      <div key={wIdx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Trophy className="w-3 h-3 text-yellow-400" />
                          <span className="text-xs text-white font-medium">{winner.username}</span>
                          <span className="text-[10px] text-slate-500">{winner.game_name}</span>
                        </div>
                        <span className="text-xs font-bold text-emerald-400">
                          {winner.pool_amount > 0 ? `${winner.pool_amount.toFixed(1)} XRP` : 'Prize'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-2.5">
                    <p className="text-[11px] text-slate-600">No winners this draw</p>
                  </div>
                )}

                {/* Total */}
                {result.total_won > 0 && (
                  <div className="px-4 py-2 bg-emerald-500/5 border-t border-emerald-500/10">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-slate-500">Total paid out</span>
                      <span className="text-xs font-bold text-emerald-400">{result.total_won.toFixed(1)} XRP</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DrawResults;
